/**
 * BoloAlertCard.tsx — v0.4.9.2.3c15 PWA
 *
 * Single BOLO alert card — mirrors GuardDashboard's AlertCard pattern
 * with color-coded BOLO chip:
 *   - missing_person: yellow chip
 *   - most_wanted: red chip
 *   - stolen_vehicle: blue chip
 *
 * c15 vs c13:
 *   - Added click-to-enlarge on the image and a "View details" button.
 *     Tapping either opens the BoloDetailModal with a LARGE image (tap
 *     image inside modal → full-screen viewer). This is the most
 *     important UX for an active alert — the agent needs to see the
 *     target face clearly before deciding what to do.
 *   - The card as a whole is NOT a single click target — the
 *     Acknowledge button must remain independently clickable.
 *   - Extended the `alert` interface with the optional fields the
 *     BoloDetailModal expects (cancelled_at, acknowledged_at,
 *     alert_created_at). Older callers can omit these; defaults are
 *     derived from issued_at where the modal needs a fallback.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, AlertOctagon, Car, User, MapPin, Clock, Check, RefreshCw, Maximize2, Info, X } from 'lucide-react';
import BoloDetailModal, { BoloDetailForModal } from './BoloDetailModal';
import { createPortal } from 'react-dom';

interface Props {
  alert: {
    id: string;
    alert_id: string | null;
    target_type: 'missing_person' | 'most_wanted' | 'stolen_vehicle' | string;
    target_name: string | null;
    target_description: string | null;
    target_photo_url: string | null;
    scope: string;
    country: string;
    country_code?: string | null;
    protocol: string;
    issued_at: string;
    issuing_org_name: string | null;
    alert_status: 'active' | 'cancelled' | 'acknowledged';
    // Optional fields the modal needs. If missing, we derive safe
    // fallbacks when building the modalBolol object below.
    cancelled_at?: string | null;
    acknowledged_at?: string | null;
    alert_created_at?: string;
  };
  onAcknowledge: (alertId: string | null) => Promise<void>;
  /** Called when user clicks "Force refresh" link (refreshes bolo list) */
  onRefresh?: () => void;
  /**
   * v0.4.9.2.3c20: optional "close" callback. When provided, the
   * card renders a corner X button — clicking it dismisses the
   * parent view (typically the Active view's setViewMode('home'))
   * without acknowledging the BOLO. The X is exactly the same
   * styling as the corner X on the Active view (red circle, white X)
   * so it works even on Capacitor WebView where theme tokens don't
   * resolve.
   */
  onClose?: () => void;
}

function resolveImageUrl(url: string | null | undefined): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `https://api.isentinel360.com${url}`;
  return `https://api.isentinel360.com/${url}`;
}

const TARGET_ICONS = {
  most_wanted: AlertOctagon,
  missing_person: User,
  stolen_vehicle: Car,
};

const typeLabels = (tt: (k: string) => string) => ({
  most_wanted: tt('bolo.type.mostWanted'),
  missing_person: tt('bolo.type.missingPerson'),
  stolen_vehicle: tt('bolo.type.stolenVehicle'),
});

// Color-coded BOLO chip per target type (user feedback 2026-09-21)
function getChipColors(targetType: string): { bg: string; text: string; border: string } {
  if (targetType === 'most_wanted') {
    return { bg: 'bg-red-600', text: 'text-white', border: 'border-red-700' };
  }
  if (targetType === 'stolen_vehicle') {
    return { bg: 'bg-blue-600', text: 'text-white', border: 'border-blue-700' };
  }
  // missing_person (default)
  return { bg: 'bg-yellow-500', text: 'text-white', border: 'border-yellow-600' };
}

function getShortLabel(targetType: string, tt: (k: string) => string): string {
  if (targetType === 'most_wanted') return tt('bolo.type.shortMostWanted');
  if (targetType === 'stolen_vehicle') return tt('bolo.type.shortStolenVehicle');
  return tt('bolo.type.shortMissingPerson');
}

export function BoloAlertCard({ alert, onAcknowledge, onRefresh, onClose }: Props) {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAcked, setIsAcked] = useState(false);
  // v0.4.9.2.3c15: Bolo detail modal state — opens when the agent
  // taps the image or the "View details" button. Same modal the
  // history rows use (one component, two trigger points).
  const [showDetailModal, setShowDetailModal] = useState(false);
  const Icon = TARGET_ICONS[alert.target_type as keyof typeof TARGET_ICONS] || Shield;
  typeLabels(t)[alert.target_type as keyof typeof typeLabels]
  const photoUrl = resolveImageUrl(alert.target_photo_url);
  const isCancelled = alert.alert_status === 'cancelled';
  const chipColors = getChipColors(alert.target_type);
  const shortLabel = getShortLabel(alert.target_type, t);

  // Map the card's alert object to the modal's BoloDetailForModal shape.
  // Provide safe fallbacks for optional fields the modal expects.
  const modalBolo: BoloDetailForModal = {
    id: alert.id,
    target_type: (alert.target_type as 'missing_person' | 'most_wanted' | 'stolen_vehicle'),
    target_name: alert.target_name,
    target_description: alert.target_description,
    target_photo_url: alert.target_photo_url,
    scope: alert.scope,
    country: alert.country,
    protocol: alert.protocol,
    issued_at: alert.issued_at,
    cancelled_at: alert.cancelled_at ?? null,
    issuing_org_name: alert.issuing_org_name,
    alert_status: alert.alert_status,
    acknowledged_at: alert.acknowledged_at ?? null,
    // Fall back to issued_at if alert_created_at is missing — they're
    // usually the same instant within a few seconds.
    alert_created_at: alert.alert_created_at ?? alert.issued_at,
  };

  const handleAck = async () => {
    if (!alert.alert_id) {
      console.warn('[BoloAlertCard] No alert_id yet — refresh to retry');
      if (onRefresh) onRefresh();
      return;
    }
    setIsProcessing(true);
    try {
      await onAcknowledge(alert.alert_id);
      setIsAcked(true);
    } catch (err) {
      console.error('[BoloAlertCard] ack failed:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isAcked) return null;

  return (
    // v0.4.9.2.3c20: relative container so the corner X close
    // button can sit absolute in the top-right of the card body.
    <div
      className={`relative bg-card border-2 rounded-lg p-4 pr-14 shadow-md ${
        isCancelled ? 'border-gray-400 opacity-60' : chipColors.border
      }`}
      data-testid="bolo-alert-card"
    >
      {/* v0.4.9.2.3c20: PROMINENT corner X close button on the
          card itself. Same red-circle styling as the Active view
          corner X — explicit colors (bg-red-600 text-white) so
          it's visible regardless of theme tokens. Calls onClose
          which the parent (civilRespond.tsx) wires to
          setViewMode('home'). Same effect as the view-level X but
          discoverable from any card. */}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 z-10 flex items-center justify-center w-10 h-10 rounded-full text-white bg-red-600 hover:bg-red-700 active:bg-red-800 shadow-md ring-2 ring-card transition-colors focus:outline-none focus:ring-red-300"
      aria-label={t('bolo.card.closeActiveAria')}
          data-testid="bolo-alert-card-close-button"
        >
          <X className="w-6 h-6" strokeWidth={2.5} />
        </button>
      )}

      {/* Header: color-coded BOLO chip + target type */}
      <div className="flex items-center gap-2 mb-3">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${chipColors.bg} ${chipColors.text} ${
            isCancelled ? '' : 'animate-pulse'
          }`}
          data-testid="bolo-chip"
        >
          <Shield className="w-3 h-3" />
          {t('bolo.status.active')}
        </span>
        <span className="text-xs font-semibold text-foreground">
          {shortLabel}
        </span>
        {isCancelled && (
          <span className="text-xs px-2 py-0.5 rounded bg-gray-500 text-white ml-auto">
            {t('bolo.status.cancelled')}
          </span>
        )}
      </div>

      {/* Target: photo + name + description */}
      <div className="flex items-start gap-3 mb-3">
        {photoUrl ? (
          // v0.4.9.2.3c15: image is now a button — opens the modal
          // with the large photo. On hover/focus a "Full screen" hint
          // appears so the user knows the image is interactive.
          <button
            type="button"
            onClick={() => setShowDetailModal(true)}
            className="relative w-20 h-20 rounded border border-border flex-shrink-0 group overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label={t('bolo.card.viewDetailAria')}
            data-testid="bolo-alert-image-button"
          >
            <img
              src={photoUrl}
              alt={alert.target_name || t('bolo.target.label')}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
            {/* Hover hint: maximize icon + "Full screen" — visible on
                hover or focus so it's discoverable but doesn't crowd
                the card when idle. */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 group-focus:bg-black/40 transition-colors flex items-center justify-center">
              <div className="flex flex-col items-center text-white opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity">
                <Maximize2 className="w-4 h-4" />
              <span className="text-[10px] font-semibold mt-0.5">{t('bolo.card.enlarge')}</span>
              </div>
            </div>
          </button>
        ) : (
          <div className={`w-20 h-20 rounded flex items-center justify-center flex-shrink-0 ${chipColors.bg} bg-opacity-20`}>
            <Icon className={`w-8 h-8 ${chipColors.text}`} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-base text-foreground truncate">
                  {t('bolo.target.unknown')}
          </div>
          {alert.target_description && (
            <div className="text-sm text-muted-foreground mt-1 line-clamp-3">
              {alert.target_description}
            </div>
          )}
        </div>
      </div>

      {/* Meta: scope, country, time */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3 flex-wrap">
        <span className="inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {alert.scope} · {alert.country}
        </span>
        <span className="inline-flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {new Date(alert.issued_at).toLocaleString()}
        </span>
        {alert.issuing_org_name && (
          <span>· {alert.issuing_org_name}</span>
        )}
      </div>

      {/* Protocol */}
      {alert.protocol && (
        <div className="bg-muted rounded p-2 text-sm italic mb-3">
          <div className="text-xs font-semibold text-muted-foreground mb-1">{t('bolo.field.protocol')}</div>
          {alert.protocol}
        </div>
      )}

      {/* Actions row: View details (opens modal) + Acknowledge.
          Two separate buttons side-by-side — image tap is also a
          shortcut to the modal for users who go straight for the photo. */}
      {/* Stacked on phones, side-by-side from sm up. `flex-wrap` alone was not
          enough: both buttons kept their min-content floor, so a long label
          overflowed the card rather than wrapping onto a second line. */}
      <div className="flex flex-col sm:flex-row gap-2">
        {!isCancelled && (
          <>
            {/* v0.4.9.2.3c15: "View details" button — opens the modal
                with full BOLO + large image. Sits next to Acknowledge
                so the agent can confirm the target's face BEFORE
                tapping Acknowledge. */}
            <button
              type="button"
              onClick={() => setShowDetailModal(true)}
              className="w-full sm:flex-1 min-w-0 px-3 py-2 border border-input text-foreground rounded font-medium flex items-center justify-center gap-1 hover:bg-muted"
              data-testid="bolo-view-details-button"
        aria-label={t('bolo.card.viewFullAria')}
            >
              <Info className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm truncate min-w-0">{t('bolo.card.details')}</span>
            </button>
            <button
              type="button"
              onClick={handleAck}
              disabled={isProcessing}
              className={`w-full sm:flex-1 min-w-0 px-4 py-2 text-white rounded font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${chipColors.bg} hover:opacity-90`}
              data-testid="bolo-ack-button"
            >
              {isProcessing ? (
                <span className="truncate min-w-0">{t('bolo.card.acknowledging')}</span>
              ) : (
                <>
                  <Check className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate min-w-0">{t('bolo.card.acknowledge')}</span>
                </>
              )}
            </button>
          </>
        )}
      </div>
      {/* Stuck-card countermeasure: clicking the link triggers refresh
          so the user doesn't have to wait 15s for the auto-sweeper. */}
      {!alert.alert_id && !isCancelled && (
        <button
          type="button"
          onClick={() => onRefresh?.()}
          className="w-full text-xs text-muted-foreground mt-2 hover:text-foreground hover:underline inline-flex items-center justify-center gap-1"
        >
          <RefreshCw className="w-3 h-3" />
          {t('bolo.card.waitingServer')}
        </button>
      )}

      {/* v0.4.9.2.3c15: Bolo detail modal — same component history
          rows use. Tap image inside modal → full-screen viewer. */}
      {showDetailModal && typeof document !== 'undefined' &&
        createPortal(
          <BoloDetailModal
            bolo={modalBolo}
            onClose={() => setShowDetailModal(false)}
          />,
          document.body
        )
      }
    </div>
  );
}
